import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../api/client';
import { useNotificationDispatch } from '../../context/NotificationContext';
import { TableRowSkeleton } from '../../components/admin/TableRowSkeleton';

// Hook para Debounce
function useDebounce(value: string, delay: number) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
}

interface UserData {
    id: string;
    username: string;
    email: string;
    role: 'USER' | 'ADMIN';
    createdAt: string;
}

export default function AdminUsersPage() {
    const [users, setUsers] = useState<UserData[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(10);

    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    const { showNotification } = useNotificationDispatch();

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.get('/admin/users', {
                params: {
                    search: debouncedSearchTerm,
                    page: currentPage,
                    limit: limit
                }
            });
            // A API agora retorna { users: [], total: number }
            setUsers(response.data.users || []);
            setTotal(response.data.total || 0);
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || 'Falha ao buscar usuários.';
            setError(errorMessage);
            setUsers([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [debouncedSearchTerm, currentPage, limit]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // Resetar para primeira página quando a busca mudar
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchTerm]);

    const handleDelete = async (userId: string, username: string) => {
        if (!window.confirm(`Tem certeza que deseja deletar o usuário "${username}"? Esta ação é irreversível.`)) {
            return;
        }
        try {
            // Assumindo que a API será /api/admin/users/:id
            await apiClient.delete(`/admin/users/${userId}`);
            showNotification(`Usuário "${username}" deletado com sucesso!`, 'success');
            fetchUsers(); // Atualiza a lista
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || 'Falha ao deletar o usuário.';
            showNotification(errorMessage, 'error');
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('pt-BR', {
            day: '2-digit', month: '2-digit', year: 'numeric'
        });
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                <h1 className="text-3xl font-bold text-text-primary mb-4 sm:mb-0">Gerenciar Usuários</h1>
                <input
                    type="text"
                    placeholder="Buscar por nome ou email..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="form-input w-full sm:w-72"
                />
            </div>

            {error && <p className="text-red-500 bg-red-100 p-4 rounded-md mb-4">{error}</p>}

            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <table className="min-w-full hidden md:table">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="th-cell">Usuário</th>
                            <th className="th-cell">Email</th>
                            <th className="th-cell">Cargo</th>
                            <th className="th-cell">Data de Cadastro</th>
                            <th className="th-cell text-right">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {loading ? (
                            Array.from({ length: 8 }).map((_, i) => <TableRowSkeleton key={i} columns={5} />)
                        ) : (
                            users.map(user => (
                                <tr key={user.id}>
                                    <td className="td-cell font-medium text-gray-900">{user.username}</td>
                                    <td className="td-cell text-gray-500">{user.email}</td>
                                    <td className="td-cell"><span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.role === 'ADMIN' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>{user.role}</span></td>
                                    <td className="td-cell text-gray-500">{formatDate(user.createdAt)}</td>
                                    <td className="td-cell text-right">
                                        <Link to={`/admin/users/edit/${user.id}`} className="text-indigo-600 hover:text-indigo-900 mr-4">Editar</Link>
                                        <button onClick={() => handleDelete(user.id, user.username)} className="text-red-600 hover:text-red-900">Deletar</button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                {/* Adaptação para mobile pode ser feita aqui se necessário */}
            </div>

            {/* Paginação */}
            {!loading && users.length > 0 && (
                <div className="flex justify-between items-center mt-6">
                    <p className="text-sm text-gray-700">
                        Mostrando {((currentPage - 1) * limit) + 1} a {Math.min(currentPage * limit, total)} de {total} usuários
                    </p>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Anterior
                        </button>
                        <span className="px-4 py-2 text-sm text-gray-700">
                            Página {currentPage} de {Math.ceil(total / limit)}
                        </span>
                        <button
                            onClick={() => setCurrentPage(prev => prev + 1)}
                            disabled={currentPage >= Math.ceil(total / limit)}
                            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Próxima
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}